import { HttpParams } from '@angular/common/http';
import { Component, EventEmitter, HostBinding, Inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subscription, interval, of, throwError } from 'rxjs';
import { API_BASE_URL_SERVICE } from '../../../kiene-core.config';
import { KieneApi, KieneBackendApiService } from '../../../services/backend-api/kiene-backend-api.service';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';
import { MessageService } from '../../../services/message-service/message.service';
import { ConfirmDialogInput, ConfirmDialogResult } from '../../dialogs/confirm-dialog/confirm-dialog';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { Gruppe } from '../../gruppen/classes/gruppe';
import { EinstallenService } from '../../gruppen/gruppe-einstallen/einstallen.service';
import { GruppenService } from '../../gruppen/gruppen.service';
import {
    PdfViewerDesktopService,
    PdfViewerDialogInput,
} from '../../pdf-viewer/kiene-pdf-viewer/kiene-pdf-viewer-desktop/pdf-viewer-desktop.service';
import { RinderpassScan } from '../classes/rinderpass-scan';
import { GruppeTierPassScanVerwaltungDialogComponent } from '../gruppe-tier-pass-scan-verwaltung-dialog/gruppe-tier-pass-scan-verwaltung-dialog.component';
import { FehlerhafteSeitenDialogComponent } from './fehlerhafte-seiten-dialog/fehlerhafte-seiten-dialog.component';
import { GruppeTierScanImportierenDialogComponent } from './gruppe-tier-scan-importieren-dialog/gruppe-tier-scan-importieren-dialog.component';
import { GruppeTierScanImportierenDialogData } from './gruppe-tier-scan-importieren-dialog/gruppe-tier-scan-importieren-dialog.data';
import { PassportJobLiveService } from './passport-job-live.service';

export interface Upload {
    progress: number;
    state: 'PENDING' | 'IN_PROGRESS' | 'DONE';
}

interface PassportJobRow {
    jobId: number;
    status: 'PENDING' | 'RUNNING' | 'DONE' | 'ERROR' | 'QUARANTINED';
    progressPercent?: number;
    messageText?: string;
    errorText?: string;
    grpsId?: number;
    pdfId?: number;
    resultJson?: string;
}

@Component({
    selector: 'kiene-gruppe-tier-pass-scan-verwaltung',
    templateUrl: './gruppe-tier-pass-scan-verwaltung.component.html',
    styleUrls: ['./gruppe-tier-pass-scan-verwaltung.component.scss'],
})
export class GruppeTierPassScanVerwaltungComponent implements OnInit, OnChanges, OnDestroy {
    @Input('gruppe') gruppe: Gruppe;
    /** When set, only PDFs linked to this Einstall-Tierliste are loaded (embedded stepper). */
    @Input() einstallTierlisteId: number | null = null;
    /** Compact card layout for Einstallen stepper (no dialog). */
    @Input() embedded = false;
    /** Hide entire block when no PDFs exist (after initial load). */
    @Input() hideWhenEmpty = false;
    @Output() tiereUpdated = new EventEmitter<void>();
    @Output() scansChange = new EventEmitter<number>();
    scans: RinderpassScan[];
    private scansLoaded = false;

    @HostBinding('class.pass-scan-verwaltung-host--hidden')
    get hostHidden(): boolean {
        return !this.isVisible;
    }

    @HostBinding('class.pass-scan-verwaltung-host--embedded')
    get hostEmbedded(): boolean {
        return this.embedded;
    }
    isSaving = false;
    private autoRefreshSub?: Subscription;
    private jobsByGrpsId = new Map<number, PassportJobRow>();
    private jobsByPdfId = new Map<number, PassportJobRow>();
    private doneJobsNotified = new Set<number>();

    constructor(
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl,
        private api: KieneBackendApiService,
        private messageService: MessageService,
        private dialog: MatDialog,
        public lss: LocalStorageService,
        private pdfViewer: PdfViewerDesktopService,
        private gruppenService: GruppenService,
        private passportLive: PassportJobLiveService,
        private einstallService: EinstallenService,
    ) {}

    ngOnInit(): void {
        this.loadRinderPassScans(true);
        this.loadPassportJobs();
        this.autoRefreshSub = interval(3000).subscribe(() => {
            this.loadPassportJobs();
            if (this.scans?.some(scan => !this.scanDone(scan))) {
                this.loadRinderPassScans(false);
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['gruppe'] || changes['einstallTierlisteId']) && this.gruppe?.gruppe_id) {
            this.loadRinderPassScans(true);
        }
    }

    ngOnDestroy(): void {
        this.autoRefreshSub?.unsubscribe();
        this.autoRefreshSub = undefined;
    }

    refresh(): void {
        this.loadRinderPassScans(false);
        this.loadPassportJobs();
    }

    loadRinderPassScans(showLoadingIndicator = true): void {
        if (!this.gruppe?.gruppe_id) {
            return;
        }
        const tierlisteId = Number(this.einstallTierlisteId || 0);
        if (this.embedded && tierlisteId <= 0) {
            this.scans = [];
            this.scansLoaded = true;
            this.scansChange.emit(0);
            return;
        }
        if (showLoadingIndicator) {
            this.scans = undefined;
        }
        let params = new HttpParams().set('gruppe_id', this.gruppe.gruppe_id.toString());
        if (tierlisteId > 0) {
            params = params.set('einstall_tierliste_id', String(tierlisteId));
        }
        params = params.append('orderby', 'erstellt desc');
        this.api.getAll(`${this.apiBaseUrl}rinderpassscan/read.php`, params).subscribe({
            next: resp => {
                this.scans = resp.records;
                this.scansLoaded = true;
                this.scansChange.emit(this.scans?.length ?? 0);
            },
            error: err => {
                this.scansLoaded = true;
                this.scansChange.emit(0);
                this.messageService.errorMessage('Fehler beim Laden der Rinderpassscans!', err);
            },
        });
    }

    get isVisible(): boolean {
        if (!this.hideWhenEmpty) {
            return true;
        }
        if (!this.scansLoaded) {
            return false;
        }
        return (this.scans?.length ?? 0) > 0;
    }

    private loadPassportJobs(): void {
        this.passportLive.listJobs(200).subscribe({
            next: (resp: KieneApi) => {
                const previousMap = this.jobsByGrpsId;
                const raw = (Array.isArray(resp) ? resp : resp?.records ?? []) as Record<string, unknown>[];
                const nextByGrps = new Map<number, PassportJobRow>();
                const nextByPdf = new Map<number, PassportJobRow>();
                for (const rawRow of raw ?? []) {
                    const row = this.normalizePassportJobRow(rawRow);
                    if (!row) continue;
                    if (row.grpsId && !nextByGrps.has(row.grpsId)) {
                        nextByGrps.set(row.grpsId, row);
                    }
                    if (row.pdfId && !nextByPdf.has(row.pdfId)) {
                        nextByPdf.set(row.pdfId, row);
                    }
                }
                this.jobsByGrpsId = nextByGrps;
                this.jobsByPdfId = nextByPdf;
                this.notifyTierListRefreshOnDone(previousMap, nextByGrps);
            },
            error: () => {
                // Keep scan list usable even if job endpoint is unavailable.
            },
        });
    }

    private notifyTierListRefreshOnDone(
        previousMap: Map<number, PassportJobRow>,
        nextMap: Map<number, PassportJobRow>,
    ): void {
        for (const [grpsId, currentJob] of nextMap.entries()) {
            if (!currentJob || currentJob.status !== 'DONE' || this.doneJobsNotified.has(grpsId)) {
                continue;
            }
            const previousStatus = previousMap.get(grpsId)?.status;
            if (previousStatus !== 'DONE') {
                this.doneJobsNotified.add(grpsId);
                this.tiereUpdated.emit();
                this.gruppenService.reloadGruppe();
            }
        }
    }

    openPdfDialog(selected: RinderpassScan) {
        this.pdfViewer.openPdfViewer(
            new PdfViewerDialogInput(
                'rinderpassscan/read_pdf.php',
                new HttpParams().set('grps_id', selected.grps_id?.toString()),
                selected?.dateiname || 'Rinderpassscan',
                { preferBlob: true },
            ),
        );
    }

    importScans() {
        this.resolveEinstallTierlisteIdForImport().subscribe({
            next: einstallTierlisteId => {
                const data: GruppeTierScanImportierenDialogData = {
                    gruppe: this.gruppe,
                    einstall_tierliste_id: einstallTierlisteId,
                };
                this.dialog
                    .open(GruppeTierScanImportierenDialogComponent, { data })
                    .afterClosed()
                    .subscribe(resp => {
                        if (resp) {
                            this.loadRinderPassScans();
                        }
                    });
            },
            error: err => {
                this.messageService.errorMessage('Einstall-Tierliste konnte nicht ermittelt werden!', err);
            },
        });
    }

    /** Same tierliste linking as Einstallen stepper / openPassPdfImportDialog. */
    private resolveEinstallTierlisteIdForImport(): Observable<number> {
        const fromInput = Number(this.einstallTierlisteId || 0);
        if (fromInput > 0) {
            return of(fromInput);
        }

        const gruppeId = Number(this.gruppe?.gruppe_id || 0);
        if (gruppeId <= 0) {
            return throwError(() => new Error('Gruppe fehlt.'));
        }

        this.einstallService.setCurrentGruppe(this.gruppe);

        const fromService = Number(this.einstallService.getCurrentEinstallTierlisteId() || 0);
        const serviceGruppeId = Number(this.einstallService.getCurrentGruppe()?.gruppe_id || 0);
        if (fromService > 0 && serviceGruppeId === gruppeId) {
            return of(fromService);
        }

        return this.einstallService.ensureEinstallTierliste(gruppeId, this.gruppe?.kunde_id);
    }

    openAllPassPdfsDialog(): void {
        if (!this.gruppe) {
            return;
        }
        this.dialog
            .open(GruppeTierPassScanVerwaltungDialogComponent, { minWidth: '50vw', data: this.gruppe })
            .afterClosed()
            .subscribe(() => {
                this.loadRinderPassScans(false);
                this.loadPassportJobs();
            });
    }

    restart(scan: RinderpassScan) {
        this.loadRinderPassScans();

        const input = new ConfirmDialogInput();
        input.headline = 'Import neustarten';
        input.text = 'Bitte den Prozess nur neu starten, wenn längere Zeit keine Seiten verarbeitet werden!';
        input.okButton = 'Neustart';
        this.dialog
            .open(ConfirmDialogComponent, { position: { top: '5%' }, data: input })
            .afterClosed()
            .subscribe((response: ConfirmDialogResult) => {
                if (response?.confirmed) {
                    this.isSaving = true;
                    this.api
                        .get(
                            this.apiBaseUrl + 'rinderpassscan/restart.php',
                            new HttpParams().set('grps_id', scan?.grps_id),
                        )
                        .subscribe({
                            next: () => {
                                this.messageService.infoMessage('Pässe werden neu eingelesen!');
                                this.isSaving = false;
                                this.loadPassportJobs();
                                this.loadRinderPassScans(false);
                            },
                            error: err => {
                                this.messageService.errorMessage('Fehler!', err);
                                this.isSaving = false;
                            },
                        });
                }
            });
    }

    private getJobForScan(scan: RinderpassScan): PassportJobRow | undefined {
        if (!scan) return undefined;
        const byGrps = this.jobsByGrpsId.get(Number(scan.grps_id));
        if (byGrps) return byGrps;
        const pdfId = Number(scan.pdf_id ?? 0);
        if (pdfId > 0) return this.jobsByPdfId.get(pdfId);
        return undefined;
    }

    scanDone(scan: RinderpassScan): boolean {
        const job = this.getJobForScan(scan);
        if (job?.status === 'DONE') {
            return true;
        }
        if (job?.status === 'ERROR' || job?.status === 'QUARANTINED') {
            return false;
        }
        const processed = Number(scan?.verarbeitet_anzahl ?? 0);
        const total = Number(scan?.seitenzahl_gesamt ?? 0);
        if (!Number.isFinite(processed) || !Number.isFinite(total) || total <= 0) {
            return false;
        }
        return processed >= total;
    }

    getStatusIcon(scan: RinderpassScan): string {
        const status = this.getJobForScan(scan)?.status;
        if (status === 'ERROR' || status === 'QUARANTINED') return 'error';
        if (status === 'RUNNING' || status === 'PENDING') return 'update';
        return this.scanDone(scan) ? 'done' : 'update';
    }

    getStatusTooltip(scan: RinderpassScan): string {
        const job = this.getJobForScan(scan);
        if (!job) return '';
        if (job.status === 'ERROR' || job.status === 'QUARANTINED') {
            return job.errorText || job.messageText || job.status;
        }
        return job.messageText || `${job.status}${job.progressPercent != null ? ` (${job.progressPercent}%)` : ''}`;
    }

    getStatusText(scan: RinderpassScan): string {
        const job = this.getJobForScan(scan);
        if (!job) {
            return this.scanDone(scan) ? 'Completed' : 'In progress';
        }
        if (job.status === 'ERROR' || job.status === 'QUARANTINED') {
            return `${job.status}: ${job.errorText || job.messageText || 'Failed'}`;
        }
        return job.messageText || job.status;
    }

    getJobDetailsText(scan: RinderpassScan): string {
        const job = this.getJobForScan(scan);
        if (!job) {
            return this.scanDone(scan) ? '—' : 'Kein OCR-Job verknüpft';
        }
        if (job.jobId > 0) {
            return `Job ${job.jobId} · ${job.status}`;
        }
        return job.status;
    }

    getJobProgressText(scan: RinderpassScan): string {
        const job = this.getJobForScan(scan);
        if (!job) {
            return '—';
        }
        if (job.progressPercent != null && Number.isFinite(job.progressPercent)) {
            return `${job.progressPercent}%`;
        }
        if (job.status === 'PENDING' || job.status === 'RUNNING') {
            return job.status;
        }
        return '—';
    }

    getEinstalldatumText(scan: RinderpassScan): Date | null {
        if (scan?.kp_einstalldatum) {
            return scan.kp_einstalldatum;
        }
        return scan?.erstellt || null;
    }

    private normalizePassportJobRow(raw: Record<string, unknown>): PassportJobRow | null {
        const grpsId = Number(raw['grpsId'] ?? raw['grps_id'] ?? 0);
        const pdfId = Number(raw['pdfId'] ?? raw['pdf_id'] ?? 0);
        if (
            (!Number.isFinite(grpsId) || grpsId <= 0) &&
            (!Number.isFinite(pdfId) || pdfId <= 0)
        ) {
            return null;
        }
        const progress = raw['progressPercent'] ?? raw['progress_percent'] ?? raw['progress'];
        return {
            jobId: Number(raw['jobId'] ?? raw['job_id'] ?? 0),
            grpsId: grpsId > 0 ? grpsId : undefined,
            pdfId: pdfId > 0 ? pdfId : undefined,
            status: (raw['status'] as PassportJobRow['status']) ?? 'PENDING',
            progressPercent: progress != null ? Number(progress) : undefined,
            messageText: String(raw['messageText'] ?? raw['message_text'] ?? raw['message'] ?? ''),
            errorText: String(raw['errorText'] ?? raw['error_text'] ?? raw['error'] ?? ''),
            resultJson: String(raw['resultJson'] ?? raw['result_json'] ?? ''),
        };
    }

    getPagesText(scan: RinderpassScan): string {
        const total = Number(scan?.seitenzahl_gesamt ?? 0);
        const verified = Number(scan?.ohrmarke_verifiziert_anzahl ?? 0);
        const processed = Number(scan?.verarbeitet_anzahl ?? 0);
        if (Number.isFinite(total) && total > 0 && this.scanDone(scan)) {
            if (Number.isFinite(verified) && verified >= 0) {
                return `${verified} / ${total}`;
            }
            return `${Number.isFinite(processed) ? processed : 0} / ${total}`;
        }
        if (Number.isFinite(total) && total > 0) {
            return `${Number.isFinite(processed) ? processed : 0} / ${total}`;
        }

        const job = this.getJobForScan(scan);
        if (job?.resultJson) {
            try {
                const parsed = JSON.parse(job.resultJson);
                const pagesTotal = Number(parsed?.pagesTotal ?? 0);
                if (pagesTotal > 0) {
                    return `${Number.isFinite(processed) ? processed : 0} / ${pagesTotal}`;
                }
            } catch {
                // ignore malformed json
            }
        }
        if (job?.progressPercent != null && job.progressPercent > 0) {
            return `~${job.progressPercent}%`;
        }
        if (job?.status === 'PENDING' || job?.status === 'RUNNING') {
            return '… / …';
        }
        return '— / —';
    }

    restartAllowed(scan: RinderpassScan): boolean {
        return !this.scanDone(scan); // && (erstellt < jetzt);
    }

    fehlerhafteSeitenNachtragen(scan: RinderpassScan) {
        this.dialog
            .open(FehlerhafteSeitenDialogComponent, { width: '100%', data: scan })
            .afterClosed()
            .subscribe({
                next: (result: boolean) => {
                    if (result) {
                        this.loadRinderPassScans();
                    }
                },
            });
    }

    getDownloadTooltip(scan: RinderpassScan) {
        if (this.lss.isCurrentUserPhitoUser()) {
            return `Download ${scan.dateiname} pdf_id: ${scan.pdf_id}`;
        }
        return 'Download';
    }

    public deleteScan(scan: RinderpassScan): void {
        const input = new ConfirmDialogInput();
        input.headline = 'Scan löschen';
        input.text = 'Soll der Scan "' + scan.dateiname + '" wirklich gelöscht werden?';
        input.okButton = 'Löschen';

        this.dialog
            .open(ConfirmDialogComponent, { data: input })
            .afterClosed()
            .subscribe((result: ConfirmDialogResult) => {
                if (result?.ok && result?.confirmed) {
                    this.api.deleteEntity(this.apiBaseUrl + 'rinderpassscan/delete.php', scan).subscribe({
                        next: () => {
                            this.loadRinderPassScans();
                        },
                        error: err => {
                            this.messageService.errorMessage('Fehler beim Löschen des Scans!', err);
                        },
                    });
                }
            });
    }
}
